import { isAxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { refreshSession } from '@/services/auth/session';
import { useAuthStore } from '@/stores/auth.store';
import { toApiError } from './api-error';
import { createHttp } from './create-http';

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

/**
 * The client every feature uses. Adds the bearer token, and on a 401 refreshes the session once
 * and replays the request. All rejections are ApiError.
 */
export const apiClient = createHttp(30_000);

apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.set('Authorization', `Bearer ${token}`);
  return config;
});

apiClient.interceptors.response.use(undefined, async (error: unknown) => {
  const config = isAxiosError(error) ? (error.config as RetriableConfig | undefined) : undefined;
  if (isAxiosError(error) && error.response?.status === 401 && config && !config._retried) {
    config._retried = true;
    const accessToken = await refreshSession();
    if (accessToken) return apiClient.request(config);
  }
  throw toApiError(error);
});

/** Typed helpers that unwrap `response.data`. */
export const http = {
  get: async <T>(url: string, config?: AxiosRequestConfig) =>
    (await apiClient.get<T>(url, config)).data,
  post: async <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
    (await apiClient.post<T>(url, body, config)).data,
  put: async <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
    (await apiClient.put<T>(url, body, config)).data,
  patch: async <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
    (await apiClient.patch<T>(url, body, config)).data,
  delete: async <T = void>(url: string, config?: AxiosRequestConfig) =>
    (await apiClient.delete<T>(url, config)).data,
};
